import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import './AdminDetails.scss'
import { BreadCrumb, HorizontalLoader } from '../../../../../Components'
import adminManagementService, {
  adminFullName,
  adminStatusLabel,
} from '../../../../../Services/adminManagementService'
import {
  ADMIN_SCOPE,
  AdminUser,
  Platform,
  PlatformEnvironment,
  PlatformGrant,
  Role,
} from '../../../../../Models/AdminManagement'
import PlatformGrantMatrix from '../Components/PlatformGrantMatrix'
import RoleCheckList from '../Components/RoleCheckList'
import useCurrentAdmin from '../../../../../Hooks/Queries/AdminManagement/useCurrentAdmin'
import Swal from 'sweetalert2'

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().every((v, i) => v === [...b].sort()[i])

const grantKey = (g: PlatformGrant) => `${g.platform}:${g.environment}`

const sameGrants = (a: PlatformGrant[], b: PlatformGrant[]) =>
  sameSet(a.map(grantKey), b.map(grantKey))

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : '—')

const AdminDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { admin: currentAdmin, canManageAdmins } = useCurrentAdmin()

  const [admin, setAdmin] = useState<AdminUser | null>(null)
  const [roles, setRoles] = useState<Role[]>([])
  const [platforms, setPlatforms] = useState<Platform[]>([])
  const [environments, setEnvironments] = useState<PlatformEnvironment[]>([])

  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [savingRoles, setSavingRoles] = useState(false)
  const [savingPlatforms, setSavingPlatforms] = useState(false)
  const [togglingStatus, setTogglingStatus] = useState(false)

  // Draft state for the two independently-saved sections
  const [draftRoles, setDraftRoles] = useState<string[]>([])
  const [draftGrants, setDraftGrants] = useState<PlatformGrant[]>([])

  const isSelf = !!admin && currentAdmin?.id === admin.id

  // Adopt a server response as the new source of truth for both drafts.
  const adopt = (next: AdminUser) => {
    setAdmin(next)
    setDraftRoles(next.roles ?? [])
    setDraftGrants(next.platforms ?? [])
  }

  const fetchAdmin = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setLoadError('')
    try {
      const [user, meta] = await Promise.all([
        adminManagementService.getUserById(id),
        adminManagementService.getMetadata(),
      ])
      adopt(user)
      setRoles(meta.roles ?? [])
      setPlatforms(meta.platforms ?? [])
      setEnvironments(meta.environments ?? [])
    } catch (e) {
      setAdmin(null)
      setLoadError((e as Error)?.message || 'Failed to load administrator.')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchAdmin()
  }, [fetchAdmin])

  const rolesDirty = !!admin && !sameSet(draftRoles, admin.roles ?? [])
  const grantsDirty = !!admin && !sameGrants(draftGrants, admin.platforms ?? [])

  const handleSaveRoles = async () => {
    if (!admin) return
    if (draftRoles.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Role Required',
        text: 'An administrator must hold at least one role.',
      })
      return
    }

    const escalating =
      draftRoles.includes(ADMIN_SCOPE.SUPER_ADMIN) &&
      !(admin.roles ?? []).includes(ADMIN_SCOPE.SUPER_ADMIN)

    if (escalating) {
      const confirmed = await Swal.fire({
        title: 'Grant super admin?',
        text: `${adminFullName(admin)} will be able to create, disable and re-permission every other administrator, including you.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e873a8',
        cancelButtonColor: '#82858a',
        confirmButtonText: 'Yes, Grant',
      })
      if (!confirmed.isConfirmed) return
    }

    setSavingRoles(true)
    try {
      const updated = await adminManagementService.updateUserRoles(admin.id, draftRoles)
      adopt(updated)
      Swal.fire({
        icon: 'success',
        title: 'Roles Updated',
        text: 'Role assignments saved.',
        confirmButtonColor: '#e873a8',
      })
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: (e as Error)?.message || 'Failed to update roles.',
      })
    } finally {
      setSavingRoles(false)
    }
  }

  const handleSavePlatforms = async () => {
    if (!admin) return

    setSavingPlatforms(true)
    try {
      const updated = await adminManagementService.updateUserPlatforms(admin.id, draftGrants)
      adopt(updated)
      Swal.fire({
        icon: 'success',
        title: 'Access Updated',
        text: 'Platform access saved.',
        confirmButtonColor: '#e873a8',
      })
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: (e as Error)?.message || 'Failed to update platform access.',
      })
    } finally {
      setSavingPlatforms(false)
    }
  }

  const handleToggleStatus = async () => {
    if (!admin) return
    const disabling = !admin.isDisabled

    const confirmed = await Swal.fire({
      title: disabling ? 'Disable Admin?' : 'Enable Admin?',
      text: disabling
        ? `${adminFullName(admin)} will immediately lose access to every platform.`
        : `${adminFullName(admin)} will regain the access listed below.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e873a8',
      cancelButtonColor: '#82858a',
      confirmButtonText: disabling ? 'Yes, Disable' : 'Yes, Enable',
    })
    if (!confirmed.isConfirmed) return

    setTogglingStatus(true)
    try {
      const updated = disabling
        ? await adminManagementService.disableUser(admin.id)
        : await adminManagementService.enableUser(admin.id)
      adopt(updated)
      Swal.fire({
        icon: 'success',
        title: disabling ? 'Disabled' : 'Enabled',
        text: `Administrator is now ${disabling ? 'disabled' : 'active'}.`,
        confirmButtonColor: '#e873a8',
      })
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: (e as Error)?.message || 'Failed to update status.',
      })
    } finally {
      setTogglingStatus(false)
    }
  }

  if (loading) {
    return (
      <div className='AdminDetails'>
        <div className='container mt-4'>
          <HorizontalLoader />
        </div>
      </div>
    )
  }

  if (!admin) {
    return (
      <div className='AdminDetails'>
        <div className='container mt-4'>
          <div className='alert alert-warning'>{loadError || 'Administrator not found.'}</div>
          <button className='btn btn-add-company' onClick={() => navigate('/admin-management')}>
            Back to Admin Management
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='AdminDetails'>
      <div className='container mt-4'>
        <div className='container'>
          <BreadCrumb
            title='Admin Details'
            parent='Admin Management'
            parentUrl='admin-management'
            showTitle={false}
          />

          {/* Top Header Card */}
          <div className='card mb-3'>
            <div className='card-body'>
              <div className='row align-items-center'>
                <div className='col-md-8'>
                  <div className='d-flex align-items-center gap-2'>
                    <span className='account-username'>{adminFullName(admin)}</span>
                    <span className={admin.isDisabled ? 'accont-status-inactive' : 'accont-status'}>
                      {adminStatusLabel(admin)}
                    </span>
                  </div>
                  <div className='account-plan'>
                    {admin.email}
                    {admin.roles?.length ? ` • ${admin.roles.join(', ')}` : ''}
                  </div>
                </div>
                <div className='col-md-4 text-end'>
                  {canManageAdmins && !isSelf && (
                    <button
                      type='button'
                      className={admin.isDisabled ? 'btn-activate' : 'btn-deactivate'}
                      onClick={handleToggleStatus}
                      disabled={togglingStatus}
                    >
                      {admin.isDisabled ? 'Enable' : 'Disable'}
                      {togglingStatus && <span className='spinner-border spinner-border-sm ms-2' />}
                    </button>
                  )}
                  {isSelf && <span className='text-muted small'>This is your own account.</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Admin Information — read-only: the API exposes no name/email update */}
          <div className='card mb-3'>
            <div className='card-body'>
              <div className='section-title'>Admin Information</div>
              <div className='row'>
                <div className='col-md-4 form-group mb-3'>
                  <label>First Name</label>
                  <input
                    type='text'
                    className='form-control'
                    value={admin.firstName || '—'}
                    readOnly
                    disabled
                  />
                </div>
                <div className='col-md-4 form-group mb-3'>
                  <label>Last Name</label>
                  <input
                    type='text'
                    className='form-control'
                    value={admin.lastName || '—'}
                    readOnly
                    disabled
                  />
                </div>
                <div className='col-md-4 form-group mb-3'>
                  <label>Email Address</label>
                  <input
                    type='email'
                    className='form-control'
                    value={admin.email}
                    readOnly
                    disabled
                  />
                </div>
              </div>
              <div className='row'>
                <div className='col-md-4'>
                  <div className='meta-label'>Created</div>
                  <div className='meta-value'>{formatDate(admin.createdAt)}</div>
                </div>
                <div className='col-md-4'>
                  <div className='meta-label'>Last Login</div>
                  <div className='meta-value'>{formatDate(admin.lastLoginAt)}</div>
                </div>
                {admin.isDisabled && (
                  <div className='col-md-4'>
                    <div className='meta-label'>Disabled</div>
                    <div className='meta-value'>{formatDate(admin.disabledAt)}</div>
                  </div>
                )}
              </div>
              <p className='text-muted mb-0 mt-2' style={{ fontSize: '0.75rem' }}>
                Name and email are managed by the identity provider and cannot be edited here.
              </p>
            </div>
          </div>

          {/* Roles */}
          <div className='card mb-3'>
            <div className='card-body'>
              <div className='section-title'>Roles</div>
              <p className='text-muted mb-2' style={{ fontSize: '0.8rem' }}>
                Roles this administrator holds:
              </p>

              <RoleCheckList
                roles={roles}
                value={draftRoles}
                onChange={setDraftRoles}
                disabled={!canManageAdmins}
                idPrefix='detail-role'
              />

              {canManageAdmins && (
                <div className='d-flex justify-content-end mt-3'>
                  <button
                    type='button'
                    className='btn btn-add-company'
                    onClick={handleSaveRoles}
                    disabled={savingRoles || !rolesDirty}
                  >
                    Save Roles{' '}
                    {savingRoles && <span className='spinner-border spinner-border-sm ms-2' />}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Platform Access */}
          <div className='card mb-3'>
            <div className='card-body'>
              <div className='section-title'>Platform Access</div>
              <p className='text-muted mb-2' style={{ fontSize: '0.8rem' }}>
                Access is granted per platform and environment:
              </p>

              <PlatformGrantMatrix
                platforms={platforms}
                environments={environments}
                value={draftGrants}
                onChange={setDraftGrants}
                disabled={!canManageAdmins}
              />

              {canManageAdmins && (
                <div className='d-flex justify-content-end mt-3'>
                  <button
                    type='button'
                    className='btn btn-add-company'
                    onClick={handleSavePlatforms}
                    disabled={savingPlatforms || !grantsDirty}
                  >
                    Save Platform Access{' '}
                    {savingPlatforms && <span className='spinner-border spinner-border-sm ms-2' />}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className='d-flex justify-content-end align-items-center gap-3 mb-5 mt-3'>
            <button
              type='button'
              className='btn btn-reset'
              onClick={() => navigate('/admin-management')}
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDetails
